import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ProgressBar from '../Components/UI/ProgressBar';
import usePost from '../Hooks/usePost';
import useGet from '../Hooks/useGet';
import API_ENDPOINTS from '../Config/api';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ClipLoader from "react-spinners/ClipLoader";
import HeaderBanner from "../Components/HeaderBanner"


export default function Questionario() {

  const { t } = useTranslation();


  const [currentStep, setCurrentStep] = useState(1);
  const { id } = useParams();
  const { fetchedData: incompleteQuestions } = useGet(API_ENDPOINTS.incompleteQuestions(id));
  const { fetchedData: aziendaInfo } = useGet(API_ENDPOINTS.aziendaInfo);
  const { fetchedData: settori } = useGet(API_ENDPOINTS.getSettori);

  
  return (
    <div>
  <HeaderBanner title={t('Compliance.title')} linkTo='/compliance' description={t('nis2.reportDescription')} />
      <ProgressBar currentStep={currentStep} totalSteps={incompleteQuestions?.sections?.length || 0} />
      {incompleteQuestions && incompleteQuestions.sections && incompleteQuestions.sections.length > 0 
        ? <QuestionnaireWizard id={id} data={incompleteQuestions} setCurrentStep={setCurrentStep} /> 
        : incompleteQuestions 
          ? <div className="mx-auto max-w-7xl mt-10 text-center"><p className="text-gray-600 text-lg">{t('questionario.allCompleted', 'Tutte le domande sono state compilate. Torna alla pagina Compliance.')}</p><a href="/compliance" className="inline-block mt-4 px-4 py-2 bg-amber-300 text-black rounded hover:bg-amber-400">{t('buttons.backToCompliance', 'Torna a Compliance')}</a></div>
          : null
      }
    </div>
  );
};


function QuestionnaireWizard({ id, data, setCurrentStep }) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const { sections } = data;
  const allQuestions = sections.flatMap((section, sectionIndex) =>
    section.questions.map((question) => ({
      ...question,
      sectionIndex,
    }))
  );

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [unlockedSectionIndices, setUnlockedSectionIndices] = useState([0]);
  const [answers, setAnswers] = useState({});

  const { postData } = usePost();
  const currentQuestion = allQuestions[currentQuestionIndex];


  

  useEffect(() => {
    if (!allQuestions.length) return;
    const sectionIndex = allQuestions[currentQuestionIndex].sectionIndex;
    setCurrentStep(sectionIndex + 1);
  }, [currentQuestionIndex, allQuestions, setCurrentStep]);

  const handleOptionChange = (questionId, optionId, isMultiple) => {
    setAnswers((prevAnswers) => {
      const prevOptions = prevAnswers[questionId] || [];
      if (isMultiple) {
        return {
          ...prevAnswers,
          [questionId]: prevOptions.includes(optionId)
            ? prevOptions.filter((id) => id !== optionId)
            : [...prevOptions, optionId],
        };
      } else {
        return {
          ...prevAnswers,
          [questionId]: [optionId],
        };
      }
    });
  };

  const handleTextChange = (questionId, text) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: text,
    }));
  };

  const isSectionComplete = () => {
    const sectionQuestions = sections[currentQuestion.sectionIndex].questions;
    return sectionQuestions.every((question) => {
      const answer = answers[question.id];
      if (question.tipo === 'libera' || question.tipo === 'testo') {
        return answer && answer.trim().length > 0;
      } else {
        return Array.isArray(answer) && answer.length > 0;
      }
    });
  };

  const handleNext = async (id) => {
    if (!isSectionComplete()) return;

    setLoading(true); // spinner ON

    const currentSectionIndex = currentQuestion.sectionIndex;
    const currentSectionQuestions = sections[currentSectionIndex].questions;

    for (const question of currentSectionQuestions) {
      const answer = answers[question.id];
      let payload = {
        domanda_id: question.id,
        opzione_id: "",
        testo_risposta: "",
        opzioni_ids: [],
      };

      if (question.tipo === 'libera' || question.tipo === 'testo') {
        payload.testo_risposta = answer;
      } else if (question.tipo === 'singola') {
        payload.opzione_id = Array.isArray(answer) && answer.length > 0 ? answer[0] : "";
      } else if (question.tipo === 'multipla') {
        payload.opzioni_ids = Array.isArray(answer) ? answer : [];
      }

      await postData(API_ENDPOINTS.saveRisposta(id), payload);
    }

    const nextSectionIndex = currentSectionIndex + 1;
    const nextFirstQuestionIndex = allQuestions.findIndex(q => q.sectionIndex === nextSectionIndex);

    if (nextFirstQuestionIndex !== -1) {
      if (!unlockedSectionIndices.includes(nextSectionIndex)) {
        setUnlockedSectionIndices(prev => [...prev, nextSectionIndex]);
      }
      setCurrentQuestionIndex(nextFirstQuestionIndex);
    } else {
    }

    setLoading(false); // spinner OFF

    if (isLastSection) {
      navigate(`/compliance`, { replace: true }); // sostituisce la route corrente
      return;
    }
  };

  const handlePrev = () => {
    const currentSectionIndex = currentQuestion.sectionIndex;
    const prevSectionIndex = currentSectionIndex - 1;

    if (prevSectionIndex >= 0) {
      const firstQIndex = allQuestions.findIndex(q => q.sectionIndex === prevSectionIndex);
      if (firstQIndex !== -1) {
        setCurrentQuestionIndex(firstQIndex);
      }
    }
  }

  const goToSection = (sectionIndex) => {
    if (unlockedSectionIndices.includes(sectionIndex)) {
      const firstQIndex = allQuestions.findIndex(q => q.sectionIndex === sectionIndex);
      if (firstQIndex !== -1) {
        setCurrentQuestionIndex(firstQIndex);
      }
    }
  };

  const isLastSection = currentQuestion.sectionIndex === sections.length - 1;
  const canComplete = isLastSection && isSectionComplete();

  return (
    <div className='mx-auto max-w-7xl mt-5'>
      <div className="flex space-x-4 mb-4">
        {sections.map((section, idx) => {
          const isUnlocked = unlockedSectionIndices.includes(idx);
          const isActive = currentQuestion.sectionIndex === idx;
          let className;
          if (isActive) className = 'px-2 py-1 rounded-md bg-amber-300 text-black';
          else if (isUnlocked) className = 'px-2 py-1 rounded-md text-black border border-black';
          else className = 'px-2 py-1 border rounded-md border-gray-300 text-gray-300 cursor-not-allowed';

          return (
            <button
              key={section.id}
              onClick={() => goToSection(idx)}
              disabled={!isUnlocked}
              className={className}
            >
              {section.name}
            </button>
          );
        })}
      </div>

      <div className="border p-4 mb-2 flex flex-col rounded-md">
        <h3 className="font-bold mb-2">{sections[currentQuestion.sectionIndex].name}</h3>
        {sections[currentQuestion.sectionIndex].questions.map((question, index) => (
          <div key={question.id} className={`flex items-center ${index % 2 !== 0 ? 'bg-gray-100' : 'bg-white'}`}>
            <div className="flex w-full p-3 items-center justify-between">
              <p className='w-[60%]'>{question.testo}</p>
              <div className="flex gap-4 items-center">
                {(question.tipo === 'libera' || question.tipo === 'testo') ? (
                  <textarea className="border rounded w-full mt-2" value={answers[question.id] || ''} onChange={(e) => handleTextChange(question.id, e.target.value)} />
                ) : (
                  question.opzioni.map((opzione) => (
                    <div key={opzione.id} className="flex items-center">
                      <input
                        type={question.tipo === 'multipla' ? 'checkbox' : 'radio'}
                        name={question.id}
                        checked={answers[question.id]?.includes(opzione.id) || false}
                        onChange={() => handleOptionChange(question.id, opzione.id, question.tipo === 'multipla')}
                      />
                      <label className="ml-2">{opzione.testo}</label>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ))}
      </div>


      <div className="flex justify-end space-x-2">
        <button
          onClick={handlePrev}
          disabled={currentQuestionIndex === 0}
          className="bg-gray-200 px-3 py-1 rounded disabled:opacity-50"
        >
          {t('buttons.previous')}
        </button>
        {canComplete ? (
            <button
              onClick={() => handleNext(id)}
              className="bg-gray-200 text-gray-700 px-3 py-1 rounded hover:bg-gray-300 transition-colors flex items-center justify-center w-full sm:w-auto sm:min-w-[120px]"
            >
              {loading ? <ClipLoader size={20} color="#000000" /> : t('buttons.viewReport')}
            </button>
        ) : (
          <button
            onClick={() => handleNext(id)}
            disabled={!isSectionComplete() || loading}
            className="bg-gray-200 text-gray-700 px-3 py-1 rounded hover:bg-gray-300 transition-colors disabled:opacity-50 flex items-center justify-center w-full sm:w-auto sm:min-w-[120px]"
          >
            {loading ? <ClipLoader size={20} color="#000000" /> : t('buttons.next')}
          </button>
        )}
      </div>


    </div>
  );
}
